import { useEffect, useMemo, useRef, useState } from "react";
import { useConfig } from "wagmi";
import { getPublicClient } from "wagmi/actions";
import { formatUnits, type Address } from "viem";
import { useT } from "../lib/i18n";
import type { RedKeel } from "../config/networks";
import type { EstadoVault } from "../hooks/useVault";
import type { Acc } from "../hooks/useAcciones";

// KeelAchicarQuoter.cuantoParaAchicar(vault, anchoTicks) -> Cotizacion
const QUOTER_ABI = [
  {
    type: "function", name: "cuantoParaAchicar", stateMutability: "view",
    inputs: [{ name: "vault", type: "address" }, { name: "anchoTicksObjetivo", type: "int24" }],
    outputs: [{
      type: "tuple", components: [
        { name: "addStable", type: "uint256" }, { name: "below", type: "int24" }, { name: "above", type: "int24" },
        { name: "anchoTicks", type: "int24" }, { name: "salida", type: "uint256" }, { name: "ok", type: "bool" },
      ],
    }],
  },
] as const;

const ERC20_ABI = [{
  type: "function", name: "balanceOf", stateMutability: "view",
  inputs: [{ name: "a", type: "address" }], outputs: [{ type: "uint256" }],
}] as const;

type Cot = { addStable: bigint; below: number; above: number; anchoTicks: number; salida: bigint; ok: boolean };

const anchoPctATicks = (totalPct: number) => Math.max(2, Math.round(Math.log(1 + totalPct / 100) / Math.log(1.0001)));
const ticksAAnchoPct = (ticks: number) => (Math.pow(1.0001, ticks) - 1) * 100;
const belowPctDe = (ticks: number) => (1 - Math.pow(1.0001, -ticks)) * 100;
const abovePctDe = (ticks: number) => (Math.pow(1.0001, ticks) - 1) * 100;
const fmt = (b: bigint, d: number, dec = 2) =>
  Number(formatUnits(b, d)).toLocaleString(undefined, { maximumFractionDigits: dec });

/**
 * EL PLAN DEL RE-CENTRADO SIMÉTRICO, CON SU APORTE.
 *
 * `cuantoParaAchicar` solo sabe de la BANDA DE RECUPERACIÓN: la que devuelve el capital sin vender.
 * Cuando la pérdida no cabe en el ancho pedido —con un 7% abajo no hay banda de ±2% que recupere
 * nada, aportes lo que aportes— el quoter contesta `ok = false`, y hasta ahora la app ofrecía en su
 * lugar un re-centrado simétrico "sin aportar" SIN comprobar que hubiera con qué llenarlo.
 *
 * Medido en un vault real (USDG/RBLX, 2026-09-17): con el precio pegado al borde de abajo, la
 * posición estaba 96,9% en RBLX, y ese "achicar sin aportar" habría desplegado 67 de 2.194 USDG de
 * valor — el 93,5% del capital se habría quedado suelto en el vault, sin cobrar una comisión, hasta
 * el siguiente barrido. Exactamente lo contrario de lo que se pulsa al achicar.
 *
 * Así que la cuenta se hace aquí: cuánto estable hay que aportar para que el activo ENTERO quepa en
 * el rango nuevo, y —si no se aporta— cuánto se quedaría fuera. Es la misma aritmética de Uniswap
 * V3 que usa el contrato (`_mintRange` despliega la parte que casa y deja libre el resto), con las
 * raíces en crudo para no arrastrar los decimales.
 */
type Plan = {
  below: number; above: number; anchoTicks: number;
  /** estable a aportar para desplegar TODO el activo, en unidades crudas */
  aporte: bigint;
  /** activo que quedaría FUERA de la posición si no se aporta nada (humano) */
  fueraActivo: number;
  /** ese sobrante, como % del valor total */
  fueraPct: number;
};

function planSimetrico(v: EstadoVault, objetivoPctTotal: number): Plan {
  const sp = Math.max(1, v.spacing);
  const half = Math.max(sp, Math.round(anchoPctATicks(objetivoPctTotal) / 2 / sp) * sp);
  const suelo = (t: number) => Math.floor(t / sp) * sp;
  const lo = suelo(v.tick - half);
  const hi = suelo(v.tick + half);
  const raiz = (t: number) => Math.pow(1.0001, t / 2);
  const s = raiz(v.tick), sa = raiz(lo), sb = raiz(hi);

  // Lo que habrá DISPONIBLE cuando la posición se cierre: lo que lleva dentro + lo suelto, menos lo
  // que el vault retiene y nunca despliega (bolsa de gas, ganancia apartada y reserva de DCA).
  const retenido0 = Number(v.gasBudget0) + Number(v.profit0) + (v.refEsToken0 ? Number(v.reserva) : 0);
  const retenido1 = Number(v.gasBudget1) + Number(v.profit1) + (!v.refEsToken0 ? Number(v.reserva) : 0);
  const have0 = Math.max(0, (v.enPosicion.a0 + v.libre.a0) * 10 ** v.token0.decimals - retenido0);
  const have1 = Math.max(0, (v.enPosicion.a1 + v.libre.a1) * 10 ** v.token1.decimals - retenido1);

  const porToken0 = 1 / s - 1 / sb; // token0 que pide una unidad de liquidez
  const porToken1 = s - sa;         // token1 que pide una unidad de liquidez

  let aporte = 0, fueraRaw = 0, decimalesActivo: number, valorTotal: number;
  if (v.refEsToken0) {
    // estable = token0, activo = token1 → la liquidez la manda el activo y falta estable
    const lActivo = porToken1 > 0 ? have1 / porToken1 : 0;
    const necesita0 = lActivo * porToken0;
    aporte = Math.max(0, necesita0 - have0);
    const lCabe = Math.min(porToken0 > 0 ? have0 / porToken0 : 0, lActivo);
    fueraRaw = Math.max(0, have1 - lCabe * porToken1);
    decimalesActivo = v.token1.decimals;
    valorTotal = have0 + have1 / (s * s); // token1 valorado en token0
  } else {
    // espejo: estable = token1, activo = token0
    const lActivo = porToken0 > 0 ? have0 / porToken0 : 0;
    const necesita1 = lActivo * porToken1;
    aporte = Math.max(0, necesita1 - have1);
    const lCabe = Math.min(porToken1 > 0 ? have1 / porToken1 : 0, lActivo);
    fueraRaw = Math.max(0, have0 - lCabe * porToken0);
    decimalesActivo = v.token0.decimals;
    valorTotal = have1 + have0 * s * s;
  }
  const fueraValor = v.refEsToken0 ? fueraRaw / (s * s) : fueraRaw * s * s;

  return {
    below: half, above: half, anchoTicks: half * 2,
    aporte: BigInt(Math.max(0, Math.floor(aporte))),
    fueraActivo: fueraRaw / 10 ** decimalesActivo,
    fueraPct: valorTotal > 0 ? (fueraValor / valorTotal) * 100 : 0,
  };
}

const CSS = `
@keyframes ach-in { from { opacity: 0 } to { opacity: 1 } }
@keyframes ach-pop { from { opacity: 0; transform: translateY(10px) scale(.97) } to { opacity: 1; transform: none } }
@keyframes ach-spin { to { transform: rotate(360deg) } }
@keyframes ach-pulse { 0%,100% { opacity: .5 } 50% { opacity: .9 } }
.ach-ov { position: fixed; inset: 0; z-index: 70; display: grid; place-items: center; padding: 16px;
  background: rgba(8,8,11,.62); backdrop-filter: blur(3px); animation: ach-in .16s ease-out; }
.ach-box { width: 100%; max-width: 480px; color: #ececf1; border-radius: 18px;
  background: linear-gradient(180deg,#191920,#141419); border: 1px solid #2b2b34;
  box-shadow: 0 1px 0 rgba(255,255,255,.05) inset, 0 10px 24px -8px rgba(0,0,0,.7), 0 40px 70px -20px rgba(0,0,0,.6);
  animation: ach-pop .22s cubic-bezier(.32,.72,0,1); overflow: hidden; }
.ach-pad { padding: 18px 20px 20px; }
.ach-x { border: 1px solid #2b2b34; background: #1e1e25; color: #9a9aa6; width: 30px; height: 30px; border-radius: 9px;
  cursor: pointer; font-size: 15px; line-height: 1; transition: transform .12s ease, background .12s ease, color .12s; }
.ach-x:hover { background: #26262e; color: #ececf1; } .ach-x:active { transform: scale(.92); }
.ach-title { font-size: 18px; font-weight: 650; letter-spacing: -.01em; display: flex; align-items: center; gap: 9px; }
.ach-ico { width: 30px; height: 30px; border-radius: 9px; display: grid; place-items: center; font-size: 15px;
  background: radial-gradient(120% 120% at 30% 20%, #ff7a4d, #d64f2a); color: #fff; box-shadow: 0 2px 8px -2px rgba(214,79,42,.6); }
.ach-sub { color: #8f8f9c; font-size: 13px; line-height: 1.55; margin: 8px 0 0; }
.ach-diag { margin: 16px 0 6px; }
.ach-track { position: relative; height: 46px; border-radius: 11px; background: #0d0d11; border: 1px solid #24242c; overflow: hidden; }
.ach-band { position: absolute; top: 8px; bottom: 8px; border-radius: 6px; transition: left .24s cubic-bezier(.32,.72,0,1), width .24s cubic-bezier(.32,.72,0,1); }
.ach-cur { background: repeating-linear-gradient(45deg,#2a2a34,#2a2a34 6px,#252530 6px,#252530 12px); }
.ach-new { background: linear-gradient(180deg, rgba(255,122,77,.9), rgba(214,79,42,.85)); box-shadow: 0 0 0 1px rgba(255,150,110,.5) inset, 0 4px 14px -4px rgba(214,79,42,.7); }
.ach-price { position: absolute; top: 3px; bottom: 3px; width: 2px; left: 50%; transform: translateX(-1px); background: #efeff4; box-shadow: 0 0 10px rgba(255,255,255,.5); }
.ach-pricelbl { position: absolute; top: -18px; left: 50%; transform: translateX(-50%); font-size: 10.5px; color: #b9b9c6; white-space: nowrap; letter-spacing: .02em; }
.ach-diaglbl { display: flex; justify-content: space-between; font-size: 11px; color: #6f6f7c; margin-top: 5px; font-variant-numeric: tabular-nums; }
.ach-lbl { font-size: 12px; color: #8f8f9c; letter-spacing: .03em; text-transform: uppercase; margin: 16px 0 8px; display: flex; justify-content: space-between; align-items: baseline; }
.ach-chips { display: flex; gap: 7px; flex-wrap: wrap; margin-bottom: 12px; }
.ach-chip { flex: 1; min-width: 56px; border: 1px solid #2b2b34; background: #16161c; color: #cfcfd8; border-radius: 10px; padding: 8px 6px;
  font: 600 13.5px ui-monospace,monospace; font-variant-numeric: tabular-nums; cursor: pointer;
  transition: transform .12s ease, border-color .12s, background .12s, color .12s; }
.ach-chip:hover { border-color: #3a3a46; } .ach-chip:active { transform: scale(.96); }
.ach-chip.on { background: rgba(214,79,42,.16); border-color: #d64f2a; color: #ffb79c; }
.ach-slider { -webkit-appearance: none; appearance: none; width: 100%; height: 5px; border-radius: 6px; outline: none; cursor: pointer;
  background: #26262e; }
.ach-slider::-webkit-slider-thumb { -webkit-appearance: none; width: 18px; height: 18px; border-radius: 50%; background: #ff7a4d; cursor: pointer;
  box-shadow: 0 0 0 4px rgba(214,79,42,.2), 0 2px 6px rgba(0,0,0,.5); transition: transform .12s ease; }
.ach-slider::-webkit-slider-thumb:active { transform: scale(1.15); }
.ach-slider::-moz-range-thumb { width: 18px; height: 18px; border: 0; border-radius: 50%; background: #ff7a4d; cursor: pointer; }
.ach-card { position: relative; margin: 14px 0 4px; border-radius: 13px; padding: 15px 16px;
  background: linear-gradient(180deg,#101014,#0d0d11); border: 1px solid #24242c; }
.ach-card.load { animation: ach-pulse 1.1s ease-in-out infinite; }
.ach-big { font-size: 25px; font-weight: 700; letter-spacing: -.02em; font-variant-numeric: tabular-nums; display: flex; align-items: baseline; gap: 7px; }
.ach-unit { font-size: 15px; font-weight: 600; color: #9a9aa6; }
.ach-tag { margin-left: auto; font-size: 12px; font-weight: 600; color: #ffb79c; background: rgba(214,79,42,.15); padding: 3px 9px; border-radius: 20px; font-variant-numeric: tabular-nums; }
.ach-row { display: flex; justify-content: space-between; align-items: center; font-size: 13px; padding: 6px 0; border-top: 1px solid #1e1e26; }
.ach-row:first-of-type { border-top: 0; margin-top: 10px; }
.ach-k { color: #8f8f9c; } .ach-v { font-variant-numeric: tabular-nums; color: #d8d8e0; }
.ach-ok { color: #4bd6a0; }
.ach-err { color: #ff7a7a; font-size: 13px; background: rgba(255,90,90,.08); border: 1px solid rgba(255,90,90,.25); border-radius: 10px; padding: 10px 12px; margin: 10px 0 2px; }
.ach-foot { display: flex; gap: 9px; justify-content: flex-end; margin-top: 16px; }
.ach-btn { border: 1px solid #2b2b34; background: #1c1c23; color: #d8d8e0; border-radius: 11px; padding: 11px 15px; font-size: 14.5px; font-weight: 600;
  cursor: pointer; transition: transform .12s ease, background .12s, opacity .12s; }
.ach-btn:hover { background: #23232b; } .ach-btn:active { transform: translateY(1px) scale(.99); }
.ach-btn.pri { flex: 1; color: #fff; border-color: transparent; font-variant-numeric: tabular-nums;
  background: linear-gradient(180deg,#ff7a4d,#e0562e); box-shadow: 0 4px 14px -4px rgba(214,79,42,.6), 0 1px 0 rgba(255,255,255,.2) inset; }
.ach-btn.pri:disabled, .ach-btn:disabled { opacity: .45; cursor: not-allowed; transform: none; }
.ach-spin { width: 15px; height: 15px; border: 2px solid rgba(255,255,255,.35); border-top-color: #fff; border-radius: 50%; display: inline-block; animation: ach-spin .7s linear infinite; vertical-align: -2px; }
@media (prefers-reduced-motion: reduce) { .ach-ov,.ach-box,.ach-band,.ach-card.load { animation: none !important; transition: none !important; } }
`;

export function ModalAchicar({ v, red, acciones, anchoActualPct, cerrar }: {
  v: EstadoVault; red: RedKeel; acciones: Acc; anchoActualPct: number; cerrar: () => void;
}) {
  const t = useT();
  const config = useConfig();
  const estable = v.refEsToken0 ? v.token0 : v.token1;
  const activo = v.refEsToken0 ? v.token1 : v.token0;

  // Ancho ACTUAL total (%). Si el vault no lo reporta, un valor razonable de respaldo.
  const actual = anchoActualPct > 0.2 ? anchoActualPct : 4;
  const min = 0.2;
  const max = Math.max(0.4, Math.round(actual * 10) / 10);
  const [objetivo, setObjetivo] = useState(() => Math.round((actual / 2) * 10) / 10);

  const [cot, setCot] = useState<Cot | null>(null);
  // El plan del re-centrado simétrico (aporte necesario y sobrante). Solo cuando el quoter no aplica.
  const [plan, setPlan] = useState<Plan | null>(null);

  const [saldoDueno, setSaldoDueno] = useState<bigint | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const idRef = useRef(0);

  // Presets como FRACCIONES del ancho actual (siempre más estrechos, escalan con la posición).
  const presets = useMemo(() => {
    const raw = [0.75, 0.5, 0.33, 0.25].map((f) => Math.round(actual * f * 100) / 100).filter((p) => p >= min && p < actual - 0.01);
    return [...new Set(raw)];
  }, [actual]); // eslint-disable-line

  // Cálculo EN VIVO (debounce) al mover el objetivo.
  useEffect(() => {
    setCargando(true); setError("");
    const id = ++idRef.current;
    const h = setTimeout(async () => {
      try {
        if (!red.quoter) throw new Error(t("Esta red no tiene la cuenta de achicar todavía."));
        const publico = getPublicClient(config, { chainId: red.idCadena });
        if (!publico) throw new Error("sin RPC");
        const r = (await publico.readContract({
          address: red.quoter as Address, abi: QUOTER_ABI, functionName: "cuantoParaAchicar",
          args: [v.direccion, anchoPctATicks(objetivo)],
        })) as Cot;
        if (idRef.current !== id) return;
        if (r.ok && r.addStable > 0n) {
          // Posición BAJO EL AGUA y RECUPERABLE en este ancho: aportar estable financia la banda de
          // recuperación (asimétrica, sin vender), que es la mejor opción cuando existe.
          setCot(r); setPlan(null);
        } else {
          // El quoter dice que no hay banda de recuperación que quepa aquí (o que no hace falta).
          // Se re-centra simétrico — pero calculando CON QUÉ se llena, que es lo que faltaba.
          const pl = planSimetrico(v, objetivo);
          setPlan(pl);
          setCot({ addStable: 0n, below: pl.below, above: pl.above, anchoTicks: pl.anchoTicks, salida: 0n, ok: true });
        }
      } catch (e) {
        if (idRef.current !== id) return;
        setCot(null);
        setError(e instanceof Error ? e.message.split("\n")[0]! : String(e));
      } finally {
        if (idRef.current === id) setCargando(false);
      }
    }, 280);
    return () => clearTimeout(h);
  }, [objetivo, red.quoter, red.idCadena, v.direccion]); // eslint-disable-line

  // Saldo del dueño en el estable: sin esto, "aporta 1.829 USDG" puede ser un botón que solo sabe
  // fallar. Se lee una vez, en paralelo con el resto.
  useEffect(() => {
    (async () => {
      try {
        const publico = getPublicClient(config, { chainId: red.idCadena });
        if (!publico) return;
        const b = (await publico.readContract({
          address: estable.address, abi: ERC20_ABI, functionName: "balanceOf", args: [v.dueno],
        })) as bigint;
        setSaldoDueno(b);
      } catch { /* si no se puede leer, no se avisa: el aviso es una ayuda, no una guardia */ }
    })();
  }, [estable.address, red.idCadena, v.dueno]); // eslint-disable-line

  /** Lo que se va a firmar: el aporte del quoter, o el del plan simétrico si el dueño lo quiere. */
  const aporteFinal = (): bigint => {
    if (!cot) return 0n;
    if (cot.addStable > 0n) return cot.addStable;
    return plan ? plan.aporte : 0n;
  };

  async function aportar() {
    if (!cot) return;
    await acciones.achicarRango(cot.below, cot.above, aporteFinal());
    cerrar();
  }

  // Geometría del diagrama.
  const nuevoPct = cot ? ticksAAnchoPct(cot.anchoTicks) : objetivo;
  const below = cot ? belowPctDe(cot.below) : nuevoPct / 2;
  const above = cot ? abovePctDe(cot.above) : nuevoPct / 2;
  const escala = Math.max(actual / 2, below, above) * 1.12;
  const px = (off: number) => 50 + (off / escala) * 50;
  const curL = px(-actual / 2), curR = px(actual / 2);
  const newL = px(-below), newR = px(above);
  const concentracion = cot ? actual / Math.max(0.1, nuevoPct) : 0;
  const aportePlan = plan ? plan.aporte : 0n;
  const aporta = !!cot && (cot.addStable > 0n || aportePlan > 0n);
  const importe = cot ? (cot.addStable > 0n ? cot.addStable : aportePlan) : 0n;
  const faltaSaldo = saldoDueno !== null && importe > saldoDueno;

  return (
    <div className="ach-ov" onClick={cerrar}>
      <style>{CSS}</style>
      <div className="ach-box" onClick={(e) => e.stopPropagation()}>
        <div className="ach-pad">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div className="ach-title"><span className="ach-ico">⤢</span>{t("Achicar el rango")}</div>
            <button className="ach-x" onClick={cerrar} aria-label="cerrar">✕</button>
          </div>
          <p className="ach-sub">
            {t("Concentra tu liquidez para cobrar más comisiones. No se vende nada de tu {a}: el rango estrecho se llena con lo que hay, y por eso a veces hay que APORTAR {s} para que entre todo.", { a: activo.symbol, s: estable.symbol })}
          </p>

          {/* Diagrama: banda actual (rayada) vs nueva (naranja), con el precio */}
          <div className="ach-diag">
            <div className="ach-track">
              <div className="ach-band ach-cur" style={{ left: `${curL}%`, width: `${curR - curL}%` }} />
              <div className="ach-band ach-new" style={{ left: `${newL}%`, width: `${Math.max(2, newR - newL)}%` }} />
              <div className="ach-price"><span className="ach-pricelbl">{t("precio")}</span></div>
            </div>
            <div className="ach-diaglbl">
              <span>{t("ahora ±{p}%", { p: (actual / 2).toFixed(1) })}</span>
              <span style={{ color: "#ffb79c" }}>{t("nuevo ±{p}%", { p: (nuevoPct / 2).toFixed(2) })}</span>
            </div>
          </div>

          {/* Selector de ancho */}
          <div className="ach-lbl"><span>{t("Ancho objetivo")}</span><span style={{ color: "#cfcfd8", fontVariantNumeric: "tabular-nums" }}>±{(objetivo / 2).toFixed(2)}%</span></div>
          {presets.length > 0 && (
            <div className="ach-chips">
              {presets.map((p) => (
                <button key={p} className={"ach-chip" + (objetivo === p ? " on" : "")} onClick={() => setObjetivo(p)}>±{(p / 2).toFixed(2)}%</button>
              ))}
            </div>
          )}
          <input className="ach-slider" type="range" min={min} max={max} step={0.1} value={objetivo}
            onChange={(e) => setObjetivo(Number(e.target.value))} />

          {/* Resultado en vivo */}
          {error ? (
            <div className="ach-err">{error}</div>
          ) : (
            <div className={"ach-card" + (cargando && !cot ? " load" : "")}>
              <div className="ach-big">
                {cargando && !cot ? <span style={{ color: "#6f6f7c" }}>·····</span>
                  : !cot ? "—"
                  : aporta ? <>{fmt(importe, estable.decimals)}<span className="ach-unit">{estable.symbol}</span></>
                  : <span style={{ fontSize: 20 }}>{t("No hace falta aportar")}</span>}
                <span className="ach-tag">{aporta ? t("aportas") : t("re-centrar")}</span>
              </div>
              <div className="ach-row"><span className="ach-k">{t("Rango resultante")}</span><span className="ach-v">±{(nuevoPct / 2).toFixed(2)}%</span></div>
              <div className="ach-row">
                <span className="ach-k">{t("Sales con")}</span>
                <span className="ach-v ach-ok">{!cot ? "—" : cot.salida > 0n ? `${fmt(cot.salida, estable.decimals, 0)} ${estable.symbol}` : t("tu capital, más lo que aportes")}</span>
              </div>
              <div className="ach-row"><span className="ach-k">{t("Tu {a}", { a: activo.symbol })}</span><span className="ach-v ach-ok">{t("no se vende ✓")}</span></div>
              {/* LO QUE ENTRA DE VERDAD. Achicar no vende nada, así que el rango estrecho solo se
                  llena hasta donde llegue el lado que falta: sin aportar, el resto se queda suelto
                  en el vault sin cobrar comisiones hasta el próximo barrido. */}
              {plan && (
                <div className="ach-row">
                  <span className="ach-k">{t("Entra en la posición")}</span>
                  <span className="ach-v ach-ok">{t("todo tu capital ✓")}</span>
                </div>
              )}
              {concentracion >= 1.15 && (
                <div className="ach-row"><span className="ach-k">{t("Concentración")}</span><span className="ach-v">{concentracion.toFixed(1)}× {t("más densa")}</span></div>
              )}
            </div>
          )}

          {faltaSaldo && (
            <div className="ach-err">
              {t("Te faltan {n} {s} en la wallet para este aporte.", {
                n: fmt(importe - (saldoDueno ?? 0n), estable.decimals, 2), s: estable.symbol,
              })}
            </div>
          )}

          <div className="ach-foot">
            <button className="ach-btn" onClick={cerrar}>{t("Cancelar")}</button>
            <button className="ach-btn pri" onClick={aportar} disabled={!cot || cargando || !!acciones.haciendo || faltaSaldo}>
              {acciones.haciendo === "achicarRango"
                ? <><span className="ach-spin" /> {t("Achicando…")}</>
                : !cot ? t("Achicar")
                : aporta ? t("Aportar {n} {s} y achicar", { n: fmt(importe, estable.decimals), s: estable.symbol })
                : t("Achicar a ±{p}%", { p: (nuevoPct / 2).toFixed(2) })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
